// ─────────────────────────────────────────────────────────────────────
// SINGLE MATCHES AS AN ARCHIVE ENTRY.
//
// The browser's `combinedArchive` appends one VIRTUAL tournament holding every
// single match, so the profile, the ELO graph and the Matches tab can treat
// them like any other tournament. `getPlayerTournamentHistory` then picks out
// the member's own.
//
// Moved here from apps/web/src/state/useDerivations.ts because the API has to
// build the SAME archive to compute the same history. Two implementations of
// this would drift, and the drift would look like "my single matches vanished"
// (CLAUDE.md: never fork a shared engine).
//
// MOVED VERBATIM — the body below is the original, re-indented only.
//
// ⚠️ IT DERIVES `date` AND `timeMinutes` FROM THE RUNNING PROCESS'S TIMEZONE
// (`toISOString().split('T')[0]`, `new Date(...).getHours()`). In a browser in
// Norway that is the venue's local time; on a server in UTC it is not, so the
// two disagree for matches played in the hours around local midnight. That is a
// pre-existing behaviour, preserved deliberately rather than corrected inside a
// migration — see docs/architecture/DOMAIN_RULES.md on characterizing rather than fixing,
// and the filed issue.
// ─────────────────────────────────────────────────────────────────────

/** One archive item, or null when the club has no single matches. */
export function buildSingleMatchesArchiveEntry(
  singleMatches: any[],
  allPlayers: any[],
): { key: string; data: any } | null {
  if (!singleMatches || singleMatches.length === 0) return null;

  // Find the date of the latest single match
  const latestPlayedAt = singleMatches.reduce((max, m) => Math.max(max, m.playedAt || 0), 0);

  // Convert single matches to a tournament format that is compatible
  // with existing code (matches, players, etc.)
  const matchesAsTournament = singleMatches.map((m) => {
    // Build slots in the same format as tournament matches
    const mkSlot = (playerId: any) => ({ kind: 'concrete', playerId });
    return {
      id: m.id,
      team1: { p1: mkSlot(m.team1.p1Id), p2: mkSlot(m.team1.p2Id) },
      team2: { p1: mkSlot(m.team2.p1Id), p2: mkSlot(m.team2.p2Id) },
      score1: m.sets ? m.sets.filter((s) => s && s.games1 > s.games2).length : 0,
      score2: m.sets ? m.sets.filter((s) => s && s.games2 > s.games1).length : 0,
      completed: true,
      phase: 'single',
      round: null,
      date: m.playedAt ? new Date(m.playedAt).toISOString().split('T')[0] : '',
      timeMinutes: m.playedAt
        ? new Date(m.playedAt).getHours() * 60 + new Date(m.playedAt).getMinutes()
        : 0,
      court: null,
      eloChange1: m.result?.t1Change ?? 0,
      eloChange2: m.result?.t2Change ?? 0,
      teamElo1Pre: m.result?.t1EloBefore != null ? Math.round(m.result.t1EloBefore) : null,
      teamElo2Pre: m.result?.t2EloBefore != null ? Math.round(m.result.t2EloBefore) : null,
      playerEloPre: m.result?.playerEloPre || {},
      // Mark as a single match so the UI can distinguish it from tournament matches
      isSingleMatch: true,
    };
  });

  // Unique players who have participated in at least one single match
  const playerIds = new Set();
  singleMatches.forEach((m) => {
    if (m.team1) {
      if (m.team1.p1Id) playerIds.add(m.team1.p1Id);
      if (m.team1.p2Id) playerIds.add(m.team1.p2Id);
    }
    if (m.team2) {
      if (m.team2.p1Id) playerIds.add(m.team2.p1Id);
      if (m.team2.p2Id) playerIds.add(m.team2.p2Id);
    }
  });
  const involvedPlayers = allPlayers.filter((p) => playerIds.has(p.id));

  return {
    key: 'virtual:single_matches',
    data: {
      archiveId: 'virtual_single_matches',
      archivedAt: latestPlayedAt,
      isArchived: true,
      isVirtual: true, // distinguishes from real tournaments
      config: {
        name: 'Enkeltkamper',
        tournamentType: 'single_matches',
      },
      players: involvedPlayers,
      matches: matchesAsTournament,
      finalRanking: null, // no winner in a collection
    },
  };
}
